import { useFormik } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import { toast } from "react-toastify";
import { useCreditCustomerSubscriptionBalanceMutation } from "../../../../store/queries/Subscription";

const useCreditBalance = (subscription, handleClose, refetch) => {
  const [isCrediting, setIsCrediting] = useState(false);
  const [creditBalance] = useCreditCustomerSubscriptionBalanceMutation();

  const emptyToNull = (value, original) => (original === "" ? null : value);

  const validation = Yup.object({
    quantity: Yup.number()
      .transform(emptyToNull)
      .required("Enter how many items to credit")
      .integer("Enter a whole number")
      .min(1, "At least 1 item")
      .max(100, "At most 100 items")
      // The service refuses a credit that would take a line past what the plan was sold
      // with rather than clamping it, so the ceiling is worth showing before the round trip.
      .max(
        Math.max(
          1,
          (subscription?.total_items ?? 0) - (subscription?.remaining_items ?? 0)
        ),
        `This plan has only ${
          (subscription?.total_items ?? 0) - (subscription?.remaining_items ?? 0)
        } item(s) that can be credited back`
      ),
    reason: Yup.string().trim().required("Enter a reason").max(500),
  });

  const formik = useFormik({
    initialValues: {
      quantity: 1,
      order_id: "",
      reason: "",
    },
    validationSchema: validation,
    onSubmit: async (values) => {
      setIsCrediting(true);

      const response = await creditBalance({
        subscription_id: subscription?.subscription_id,
        quantity: Number(values?.quantity),
        order_id: values?.order_id || null,
        reason: values?.reason,
      });

      if (response?.data?.status_code === 200) {
        toast.success(`Balance credited`);
        setIsCrediting(false);
        refetch();
        handleClose();
        return;
      }

      toast.error(
        response?.error?.data?.message ??
          response?.data?.message ??
          `Unable to credit this balance`
      );
      setIsCrediting(false);
    },
  });

  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };

  return { formik, isCrediting, getFieldError };
};

export default useCreditBalance;
